import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "../../components/ui/notify";
import { Typography, Space, Button, Card, Modal, Spin, Row, Col, Input, Switch, Select } from "antd";
import { CheckCircleFilled, CloseCircleFilled, SaveOutlined, CloseOutlined } from "@ant-design/icons";

import {
  actionUsuarioGetById,
  actionUsuarioUpdateById,
  actionUsuarioDeleteById,
  actionUsuarioPermisosGet,
  actionUsuarioPermisosUpdate,
} from "../../redux/actions/usuarios/usuarios";
import { actionCiudadesGet } from "../../redux/actions/ciudades/ciudades";
import { usePermisos } from "../../context/PermisosContext";
import { PERMISO_SOLICITAR_CAMBIOS, sinPermisosEspeciales } from "../../utils/permisosEspeciales";
import { MODULOS, ACTIONS } from "../../constants/modulos";
import { DataBadge } from "../../components/ui";
import { STATUS_PILL_HEX } from "./materias/laboral/tribunalEstatus.js";
import "../../assets/css/LaboralExpedienteDetallePage.css";
import "../../components/conciliacion/detalles/LaboralExpedienteDetallePage.css";

const { Text, Title } = Typography;

const fmtDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
};

const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "—";
  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

function Field({ label, value }) {
  return (
    <div className="laboral-exp-field">
      <span className="laboral-exp-field-label">{label}</span>
      <span className="laboral-exp-field-value">{value ?? "—"}</span>
    </div>
  );
}

const MODULO_ACTION = ACTIONS.find((a) => a.key === "modulo") || null;
const OTHER_ACTIONS = ACTIONS.filter((a) => a.key !== "modulo");

const DEFAULT_PERMISOS = Object.fromEntries(
  MODULOS.map((m) => [
    m.key,
    { ...Object.fromEntries(ACTIONS.map((a) => [a.key, false])), ciudades: null },
  ])
);

const HEADER_TABS = [
  { key: "datos", label: "Datos del usuario", icon: <i className="fa-solid fa-user" /> },
  { key: "permisos", label: "Permisos", icon: <i className="fa-solid fa-user-shield" /> },
];

export default function UsuarioDetallePage() {
  const dispatch = useDispatch();
  const { idUser } = useParams();

  const { perm } = usePermisos() || { perm: () => true };
  const canEditar = perm("usuarios", "editar");

  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [activeTab, setActiveTab] = useState("datos");

  const [editingDatos, setEditingDatos] = useState(false);
  const [editingForm, setEditingForm] = useState({});
  const [savingDatos, setSavingDatos] = useState(false);

  // Permisos state
  const [permisos, setPermisos] = useState(DEFAULT_PERMISOS);
  const [permisosOriginal, setPermisosOriginal] = useState(DEFAULT_PERMISOS);
  const [permisosLoading, setPermisosLoading] = useState(false);
  const [savingPermisos, setSavingPermisos] = useState(false);
  const [editingPermisos, setEditingPermisos] = useState(false);

  // Ciudades (catálogo, para el multiselect de ciudades por módulo)
  const ciudadesSlice = useSelector((state) => state.ciudades || {});
  const ciudadesItems = useMemo(() => ciudadesSlice?.data?.items || [], [ciudadesSlice?.data]);
  const ciudadesOptions = useMemo(
    () => ciudadesItems.map((c) => ({ label: c.nombre ?? c.code ?? `Ciudad ${c.id}`, value: c.id })),
    [ciudadesItems]
  );
  const ciudadesNombreById = useMemo(
    () => Object.fromEntries(ciudadesItems.map((c) => [c.id, c.nombre ?? c.code ?? `Ciudad ${c.id}`])),
    [ciudadesItems]
  );

  useEffect(() => {
    const yaCargadas = Array.isArray(ciudadesSlice?.data?.items) && ciudadesSlice.data.items.length > 0;
    if (!yaCargadas) dispatch(actionCiudadesGet());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const loadDetalle = useCallback(async () => {
    try {
      setLoadingDetalle(true);
      const data = await dispatch(actionUsuarioGetById(idUser));
      setDetalle(data || null);
    } catch {
      notify.error({ message: "Error al cargar", description: "No se pudo obtener el detalle del usuario." });
      setDetalle(null);
    } finally {
      setLoadingDetalle(false);
    }
  }, [dispatch, idUser]);

  useEffect(() => {
    loadDetalle();
  }, [loadDetalle]);

  const fetchPermisos = useCallback(async () => {
    setPermisosLoading(true);
    try {
      const data = await dispatch(actionUsuarioPermisosGet(idUser));
      setPermisos(data || DEFAULT_PERMISOS);
      setPermisosOriginal(data || DEFAULT_PERMISOS);
    } catch {
      setPermisos(DEFAULT_PERMISOS);
      setPermisosOriginal(DEFAULT_PERMISOS);
    } finally {
      setPermisosLoading(false);
    }
  }, [dispatch, idUser]);

  useEffect(() => {
    if (activeTab === "permisos") {
      setEditingPermisos(false);
      fetchPermisos();
    }
  }, [activeTab, fetchPermisos]);

  const handleSavePermisos = async () => {
    setSavingPermisos(true);
    try {
      // El permiso especial viaja aparte: el dict `permisos` solo admite modulos.
      await dispatch(
        actionUsuarioPermisosUpdate(idUser, sinPermisosEspeciales(permisos), !!permisos[PERMISO_SOLICITAR_CAMBIOS])
      );
      setPermisosOriginal(permisos);
      setEditingPermisos(false);
      window.dispatchEvent(new Event("permisos-refresh"));
      notify.success({ message: "Permisos guardados correctamente", placement: "bottomRight" });
    } catch (e) {
      notify.error({
        message: "No se pudieron guardar los permisos",
        description: e?.response?.data?.detail || e?.message,
        placement: "bottomRight",
      });
    } finally {
      setSavingPermisos(false);
    }
  };

  const handleCancelEditPermisos = () => {
    setPermisos(permisosOriginal);
    setEditingPermisos(false);
  };

  const togglePermiso = (module, action, value) => {
    setPermisos((prev) => ({
      ...prev,
      [module]: { ...prev[module], [action]: value },
    }));
  };

  const toggleRestringirCiudades = (module, restringido) => {
    setPermisos((prev) => ({
      ...prev,
      [module]: { ...prev[module], ciudades: restringido ? (prev[module]?.ciudades || []) : null },
    }));
  };

  const setCiudadesModulo = (module, ids) => {
    setPermisos((prev) => ({
      ...prev,
      [module]: { ...prev[module], ciudades: ids },
    }));
  };

  const startEditDatos = () => {
    setEditingForm({ name: detalle?.name || "", email: detalle?.email || "", password: "" });
    setEditingDatos(true);
  };

  const handleSaveDatos = async () => {
    const name = (editingForm.name || "").trim();
    const email = (editingForm.email || "").trim().toLowerCase();
    if (!name) {
      notify.error({ message: "Falta el nombre del usuario", placement: "bottomRight" });
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      notify.error({ message: "Correo inválido", placement: "bottomRight" });
      return;
    }
    if (editingForm.password && editingForm.password.length < 6) {
      notify.error({ message: "La contraseña debe tener al menos 6 caracteres", placement: "bottomRight" });
      return;
    }

    setSavingDatos(true);
    try {
      const payload = { name, email };
      if (editingForm.password) payload.password = editingForm.password;

      await dispatch(actionUsuarioUpdateById(idUser, payload));
      await loadDetalle();
      setEditingDatos(false);
      notify.success({
        message: "Usuario actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingDatos(false);
    }
  };

  const handleToggleActive = () => {
    const isActive = Number(detalle?.active ?? 1) === 1;
    Modal.confirm({
      title: isActive ? "Desactivar usuario" : "Activar usuario",
      content: isActive
        ? "El usuario no podrá iniciar sesión mientras esté desactivado."
        : "El usuario podrá volver a iniciar sesión.",
      okText: isActive ? "Desactivar" : "Activar",
      okType: isActive ? "danger" : "primary",
      cancelText: "Cancelar",
      centered: true,
      onOk: async () => {
        setToggling(true);
        try {
          if (isActive) {
            await dispatch(actionUsuarioDeleteById(idUser));
          } else {
            await dispatch(actionUsuarioUpdateById(idUser, { active: 1 }));
          }
          await loadDetalle();
          notify.success({ message: isActive ? "Usuario desactivado" : "Usuario activado", placement: "bottomRight" });
        } catch (e) {
          notify.error({
            message: "No se pudo actualizar",
            description: e?.response?.data?.detail || e?.message,
            placement: "bottomRight",
          });
        } finally {
          setToggling(false);
        }
      },
    });
  };

  if (loadingDetalle || !detalle) {
    return (
      <div className="lxp-root">
        <div className="lxp-wrap">
          <Card className="laboral-exp-summary-card p-2" style={{ marginTop: 16 }}>
            {loadingDetalle ? (
              <div style={{ padding: 40, textAlign: "center" }}>
                <Spin size="large" />
              </div>
            ) : (
              <Text type="secondary">Sin datos del usuario.</Text>
            )}
          </Card>
        </div>
      </div>
    );
  }

  const isActive = Number(detalle?.active ?? 1) === 1;
  const estatusPill = isActive
    ? { label: "ACTIVO", kind: "primary" }
    : { label: "INACTIVO", kind: "gray" };

  return (
    <div className="lxp-root">
      <div className="lxp-wrap">
        <div className="laboral-exp-main" style={{ background: "transparent", padding: 0, minHeight: "auto" }}>
          <Card className="laboral-exp-summary-card p-2">
            <Row align="middle" gutter={[16, 16]} justify="space-between" wrap>
              <Col flex="auto">
                <Space direction="vertical" size={8}>
                  <div className="laboral-exp-summary-title">
                    <Space align="center" wrap>
                      <div className="laboral-exp-icon-badge">
                        <i className="fa-solid fa-user" />
                      </div>
                      <Title
                        level={4}
                        style={{ margin: 0, fontSize: 28, fontWeight: 650, letterSpacing: "-0.01em", lineHeight: 1.2, color: "#0f172a" }}
                      >
                        {toTitleCase(detalle.name).toUpperCase()}
                      </Title>
                      <DataBadge
                        color={STATUS_PILL_HEX[estatusPill.kind] || estatusPill.kind}
                        className="d-inline-flex align-items-center gap-1 status-pill-badge"
                      >
                        {estatusPill.label}
                      </DataBadge>
                    </Space>
                  </div>

                  <Space size="large" wrap className="laboral-exp-summary-meta">
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-calendar-days" />
                      <Text>Creado: {fmtDateLong(detalle.datetime)}</Text>
                    </Space>
                  </Space>
                </Space>
              </Col>

              <Col flex="none">
                <Space>
                  <Button
                    danger={isActive}
                    type="primary"
                    icon={<i className={`fa-solid ${isActive ? "fa-user-slash" : "fa-user-check"}`} />}
                    style={{ borderRadius: 8 }}
                    onClick={handleToggleActive}
                    loading={toggling}
                    disabled={toggling}
                  >
                    {isActive ? "Desactivar" : "Activar"}
                  </Button>
                </Space>
              </Col>
            </Row>

            <div className="laboral-exp-header-tabs">
              {HEADER_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`laboral-exp-tab-btn ${activeTab === t.key ? "laboral-exp-tab-btn-active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {activeTab === "datos" && (
        <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ marginTop: 18 }}>
          <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="laboral-exp-icon-badge">
                <i className="fa-solid fa-id-card" />
              </div>
              <h3 className="laboral-exp-card-header-title">Datos del usuario</h3>
            </div>

            {canEditar && !editingDatos && (
              <Button onClick={startEditDatos} className="laboral-exp-btn-clean">
                Editar
              </Button>
            )}
          </div>

          {editingDatos ? (
            <div className="laboral-exp-edit-form">
              <div className="laboral-exp-edit-grid">
                <div className="laboral-exp-edit-field">
                  <span className="laboral-exp-edit-label">Nombre</span>
                  <Input
                    value={editingForm.name}
                    onChange={(e) => setEditingForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="laboral-exp-edit-field">
                  <span className="laboral-exp-edit-label">Correo</span>
                  <Input
                    value={editingForm.email}
                    onChange={(e) => setEditingForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div className="laboral-exp-edit-field">
                  <span className="laboral-exp-edit-label">Nueva contraseña</span>
                  <Input.Password
                    value={editingForm.password}
                    placeholder="Dejar en blanco para no cambiar"
                    onChange={(e) => setEditingForm((f) => ({ ...f, password: e.target.value }))}
                  />
                </div>
              </div>

              <div className="laboral-exp-edit-actions">
                <Button onClick={() => setEditingDatos(false)} className="laboral-exp-btn-clean">
                  Cancelar
                </Button>
                <Button
                  type="primary"
                  loading={savingDatos}
                  onClick={handleSaveDatos}
                  className="laboral-exp-btn-save"
                  icon={<i className="fa-solid fa-floppy-disk" />}
                >
                  Guardar
                </Button>
              </div>
            </div>
          ) : (
            <div className="laboral-exp-field-grid">
              <Field label="Nombre" value={toTitleCase(detalle?.name)} />
              <Field label="Estatus" value={isActive ? "Activo" : "Inactivo"} />
              <Field label="Correo" value={detalle?.email || "—"} />
            </div>
          )}
        </Card>
        )}

        {activeTab === "permisos" && (
          <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ marginTop: 18 }}>
            <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="laboral-exp-icon-badge">
                  <i className="fa-solid fa-user-shield" />
                </div>
                <h3 className="laboral-exp-card-header-title">Permisos del usuario</h3>
              </div>
              {canEditar && !editingPermisos && !permisosLoading && (
                <Button
                  icon={<i className="fa-solid fa-pen" />}
                  onClick={() => setEditingPermisos(true)}
                  className="laboral-exp-btn-save"
                >
                  Editar permisos
                </Button>
              )}
            </div>

            <p style={{ fontSize: 13, color: "#64748b", margin: "4px 0 20px" }}>
              {editingPermisos
                ? "Activa o desactiva los permisos y guarda los cambios."
                : "Define qué acciones puede realizar este usuario en cada módulo del sistema."}
            </p>

            {permisosLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                <Spin size="large" />
              </div>
            ) : (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                        <th style={{ textAlign: "left", padding: "10px 16px", fontWeight: 700, fontSize: 11, letterSpacing: "0.07em", color: "#475569", textTransform: "uppercase", width: "22%" }}>
                          Módulo
                        </th>
                        {MODULO_ACTION && (
                          <th key={MODULO_ACTION.key} style={{ textAlign: "center", padding: "10px 16px", fontWeight: 700, fontSize: 11, letterSpacing: "0.07em", color: "#475569", textTransform: "uppercase" }}>
                            {MODULO_ACTION.label}
                          </th>
                        )}
                        {OTHER_ACTIONS.map((a) => (
                          <th key={a.key} style={{ textAlign: "center", padding: "10px 16px", fontWeight: 700, fontSize: 11, letterSpacing: "0.07em", color: "#475569", textTransform: "uppercase" }}>
                            {a.label}
                          </th>
                        ))}
                        <th style={{ textAlign: "left", padding: "10px 16px", fontWeight: 700, fontSize: 11, letterSpacing: "0.07em", color: "#475569", textTransform: "uppercase", width: "26%" }}>
                          Ciudades
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {MODULOS.map((m, idx) => (
                        <tr
                          key={m.key}
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            background: idx % 2 === 0 ? "#fff" : "#fafbfc",
                          }}
                        >
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, color: "#0f172a" }}>
                              <span style={{ color: "#64748b", fontSize: 15 }}>{m.icon}</span>
                              {m.label}
                            </div>
                          </td>
                          {MODULO_ACTION && (
                            <td style={{ textAlign: "center", padding: "14px 16px" }}>
                              {editingPermisos ? (
                                <Switch
                                  checked={permisos[m.key]?.[MODULO_ACTION.key] ?? false}
                                  onChange={(val) => togglePermiso(m.key, MODULO_ACTION.key, val)}
                                  size="small"
                                  style={permisos[m.key]?.[MODULO_ACTION.key] ? { backgroundColor: "#0A539A" } : {}}
                                />
                              ) : (
                                permisos[m.key]?.[MODULO_ACTION.key]
                                  ? <CheckCircleFilled style={{ color: "#22c55e", fontSize: 18 }} />
                                  : <CloseCircleFilled style={{ color: "#ef4444", fontSize: 18 }} />
                              )}
                            </td>
                          )}
                          {OTHER_ACTIONS.map((a) => (
                            <td key={a.key} style={{ textAlign: "center", padding: "14px 16px" }}>
                              {editingPermisos ? (
                                <Switch
                                  checked={permisos[m.key]?.[a.key] ?? false}
                                  onChange={(val) => togglePermiso(m.key, a.key, val)}
                                  size="small"
                                  style={permisos[m.key]?.[a.key] ? { backgroundColor: "#0A539A" } : {}}
                                />
                              ) : (
                                permisos[m.key]?.[a.key]
                                  ? <CheckCircleFilled style={{ color: "#22c55e", fontSize: 18 }} />
                                  : <CloseCircleFilled style={{ color: "#ef4444", fontSize: 18 }} />
                              )}
                            </td>
                          ))}
                          <td style={{ padding: "14px 16px" }}>
                            {editingPermisos ? (
                              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                <Space size={6} align="center">
                                  <Switch
                                    checked={permisos[m.key]?.ciudades !== null && permisos[m.key]?.ciudades !== undefined}
                                    onChange={(val) => toggleRestringirCiudades(m.key, val)}
                                    size="small"
                                    style={permisos[m.key]?.ciudades != null ? { backgroundColor: "#0A539A" } : {}}
                                  />
                                  <span style={{ fontSize: 12, color: "#64748b" }}>Restringir por ciudad</span>
                                </Space>
                                {permisos[m.key]?.ciudades != null && (
                                  <Select
                                    mode="multiple"
                                    size="small"
                                    allowClear
                                    placeholder="Ninguna ciudad seleccionada"
                                    style={{ width: "100%", minWidth: 220 }}
                                    options={ciudadesOptions}
                                    value={permisos[m.key]?.ciudades || []}
                                    onChange={(ids) => setCiudadesModulo(m.key, ids)}
                                    maxTagCount="responsive"
                                  />
                                )}
                              </Space>
                            ) : permisos[m.key]?.ciudades == null ? (
                              <span style={{ fontSize: 12, color: "#64748b" }}>Todas las ciudades</span>
                            ) : permisos[m.key].ciudades.length === 0 ? (
                              <span style={{ fontSize: 12, color: "#ef4444" }}>Ninguna ciudad</span>
                            ) : (
                              <span style={{ fontSize: 12, color: "#0f172a" }}>
                                {permisos[m.key].ciudades.map((id) => ciudadesNombreById[id] || id).join(", ")}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {editingPermisos && (
                  <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                    <Button icon={<CloseOutlined />} onClick={handleCancelEditPermisos} disabled={savingPermisos}>
                      Cancelar
                    </Button>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      loading={savingPermisos}
                      onClick={handleSavePermisos}
                      className="laboral-exp-btn-save"
                    >
                      Guardar permisos
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card>
        )}

        {activeTab === "permisos" && (
          <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ marginTop: 18 }}>
            <div className="laboral-exp-card-header">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="laboral-exp-icon-badge">
                  <i className="fa-solid fa-star" />
                </div>
                <h3 className="laboral-exp-card-header-title">Permisos especiales</h3>
              </div>
            </div>

            <p style={{ fontSize: 13, color: "#64748b", margin: "4px 0 12px" }}>
              Permisos que no dependen de un módulo en particular.
            </p>
            {permisos.es_administracion && (
              <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 12px" }}>
                Este usuario es de administración: tiene acceso total a todos los módulos y no se le pueden restringir permisos.
              </p>
            )}

            {permisosLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <Spin />
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0" }}>
                <Switch
                  checked={!!permisos.es_administracion || !!permisos[PERMISO_SOLICITAR_CAMBIOS]}
                  disabled={!!permisos.es_administracion || !editingPermisos}
                  onChange={(val) => setPermisos((prev) => ({ ...prev, [PERMISO_SOLICITAR_CAMBIOS]: val }))}
                  style={permisos.es_administracion || permisos[PERMISO_SOLICITAR_CAMBIOS] ? { backgroundColor: "#0A539A" } : {}}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>Puede solicitar cambios al sistema</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    Permite enviar solicitudes de cambio o reportar errores directamente al proveedor del software.
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
